import {CursorType} from '@/enums/diagrams';
import {MarkerCursorData} from '../cursorDiagramState';
import {MarkerCursorLabelModel} from './labelModels/markerCursorLabelModel';
import {MarkerCursor} from './markerCursor';
import {uuidv4} from '@u/uuidUtils';
import {MarkerSeriesModulationPoints} from '@/interfaces/markers';
import {CursorPlotLine} from './cursorPlotLine';
import {CursorManager} from './cursorManager';

/*
  Класс модуляционного курсора
*/
export class ModulationCursor extends MarkerCursor {
  // Тип курсора
  type: CursorType = CursorType.ModulationCursor;

  // Префикс идентификатора модуляционной линии
  private modulationLineIdPrefix = 'md-line-';

  // Количество модуляционных линий (в каждую сторону)
  linesCount = 5;

  // Списоки модуляционных линий - влево и вправо
  private modulationCursorLeftLines: CursorPlotLine[] = [];

  private modulationCursorRightLines: CursorPlotLine[] = [];

  // Идентификаторы серий с модуляционными маркерами
  private modulationMarkerSeries: MarkerSeriesModulationPoints[] = [];

  // Список отметок для модуляционных маркеров
  private modulationTooltips: any[] = [];

  // Перемещаемая модуляционная линия
  private dragModulationLine: CursorPlotLine | null = null;

  constructor(
    labelModel: MarkerCursorLabelModel | null = null,
    applyCallback: Function | null = null,
    color = '#00FF00FF',
    directions: string[] = ['left', 'right'],
  ) {
    super(labelModel, applyCallback, color, directions);
  }

  /*
    Инициализация курсора
  */
  init(
    cursorManager: CursorManager,
    chart: any,
    bindedSeriesIds: string[] = [],
    isShowLine = true,
    isShowTooltips = false,
    cursorData: MarkerCursorData | null | undefined,
  ) {
    // инициализируем модуляционные линии
    for (let index = 0; index < this.linesCount; index++) {
      if (this.directions.includes('left'))
        this.modulationCursorLeftLines.push(
          new CursorPlotLine(
            chart,
            {
              id: this.getModulationLineId(index, 'left'),
              value: undefined,
              width: 3,
              color: this.color, //'#00FF00FF', //'rgba(0, 255, 0, 1)',
              zIndex: 9998,
            },
            true,
            -(index + 1),
          ),
        );

      if (this.directions.includes('right'))
        this.modulationCursorRightLines.push(
          new CursorPlotLine(
            chart,
            {
              id: this.getModulationLineId(index, 'right'),
              value: undefined,
              width: 3,
              color: this.color, //'#00FF00FF', //'rgba(0, 255, 0, 1)',
              zIndex: 9998,
            },
            true,
            index + 1,
          ),
        );
    }

    super.init(cursorManager, chart, bindedSeriesIds, isShowLine, isShowTooltips, cursorData);
  }

  /*
    Инициализация данных курсора
  */
  protected initCursorData(cursorData: MarkerCursorData | null | undefined) {
    super.initCursorData(cursorData);
    this.redrawModulation();
  }

  /*
    Получить идентификатор модуляционной линии
  */
  private getModulationLineId(index: number, direction: string) {
    return `${this.modulationLineIdPrefix}${direction}-${index}-${this.id}`;
  }

  /*
    Действия при активации курсора
  */
  apply(point: any) {
    super.apply(point);
    this.redrawModulation();
  }

  /*
    Получение идентификатора серии модуляционных маркеров
  */
  protected newModulationSeriesId(id: string) {
    return `modulationMarkerSeries-${id}`;
  }

  /*

  */
  protected onUpHandler() {
    super.onUpHandler();
    if (this.dragModulationLine && this.dragModulationLine.isDrag())
      this.dragModulationLine.stopDrag();
  }

  /*
    Действия при перемещении
  */
  protected onMoveHandler(e: any) {
    super.onMoveHandler(e);

    this.dragModulationLine = this.getDragMoulationLine();
    if (this.dragModulationLine && this.firstCopiedPoint && this.copiedPoint) {
      const newXValue = this.dragModulationLine.getTranslatedXValue(e.pageX);
      const lineIndex = this.dragModulationLine.index;
      if (!lineIndex) return;
      if (!newXValue || !this.checkForDirection(this.firstCopiedPoint, {x: newXValue})) return;
      this.dragModulationLine.translateX(e.pageX, e.chartX);
      const middleX = this.firstCopiedPoint.x + (newXValue - this.firstCopiedPoint.x) / lineIndex;
      super.apply({x: middleX, y: 0});
    }

    // Переносим все модуляционные линии
    if (
      this.firstCursorPlotLine.isDrag() ||
      this.cursorPlotLine.isDrag() ||
      this.dragModulationLine
    )
      this.redrawModulation();

    // Отправка уведомления для перерисовки частей интерфейса на графике
    if (this.dragModulationLine) this.notify();
  }

  getDragMoulationLine() {
    const dragLines = this.modulationCursorLeftLines
      .concat(this.modulationCursorRightLines)
      .filter(x => x.isDrag());
    if (dragLines.length) return dragLines[0];
    return null;
  }

  /*
   Перерисовать модуляционные линии и (если нужно) серию с маркерами
  */
  private redrawModulation(redrawMarkers = true) {
    if (this.firstCopiedPoint && this.bandWidth !== null) {
      const extremes = this.chart.xAxis[0].getExtremes();

      const value = this.firstCopiedPoint.x;
      const bandWidth = this.bandWidth;

      const modulationSeries: MarkerSeriesModulationPoints[] = [];
      // для всех сравниваемых графиков находим точки пересечения с курсором и запоминаем их, вместе с номерами соответствующих осей
      this.bindedSeries.forEach((series: any) => {
        const s = this.chart.series.find((s: any) => s.userOptions.id == series.options.id);
        if (!s.visible) return;
        const modulationPointsChart = this.redrawModulationLines(
          value,
          bandWidth,
          this.modulationCursorLeftLines,
          extremes,
          -1,
          series,
        ).concat(
          this.redrawModulationLines(
            value,
            bandWidth,
            this.modulationCursorRightLines,
            extremes,
            1,
            series,
          ),
        );

        modulationSeries.push({
          chartSeriesId: series.options.id,
          yAxis: series.options.yAxis,
          points: modulationPointsChart,
        });
      });

      if (!redrawMarkers) return;

      this.modulationMarkerSeries.forEach((series: any) => {
        const findIndex = modulationSeries.findIndex(
          (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
        );
        if (findIndex === -1) this.chart.get(series.id)?.remove();
        else modulationSeries[findIndex] = {...modulationSeries[findIndex], id: series.id};
      });

      this.modulationMarkerSeries = [...modulationSeries];
      this.modulationMarkerSeries.forEach((series: any) => {
        series.id = series.id !== undefined ? series.id : this.newModulationSeriesId(uuidv4());
        this.drawMarkersModulationSeries(series.id, series.yAxis, series.points);
      });
    }
  }

  /*
    Рисуем серию с маркерами
  */
  protected drawMarkersModulationSeries(
    markerSeriesId: string,
    axisNumber: number,
    markerPoints: any[],
  ) {
    let markerSeries = this.chart.get(markerSeriesId);

    if (!markerSeries) {
      // если серия с маркерами не найдена, то создаем ее (без перерисовки)
      markerSeries = this.chart.addSeries(
        {
          id: markerSeriesId,
          showInLegend: false,
          type: 'scatter',
          data: [],
          lineWidth: 0,
          zIndex: 9999,
          marker: {
            symbol: 'circle',
            fillColor: this.color,
            lineColor: this.color,
            radius: 5,
            enabled: true,
            zIndex: 9999,
          },
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
            inactive: {
              enabled: false,
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          yAxis: axisNumber,
        },
        false,
      );
    }

    // нужно обязательно сортировать данные перед добавлением в серию
    markerPoints.sort((a: any, b: any) => a.x - b.x);

    // добавляем точки маркеров в серию (с перерисовкой)
    markerSeries.setData(markerPoints.map(p => ({x: p.x, y: p.y})));
  }

  /*
    Логика перерисовки модуляционных линий (отдельный метод для сокращения похожего кода)
  */
  private redrawModulationLines(
    value: number,
    bandWidth: number,
    modulationLines: CursorPlotLine[],
    extremes: any,
    direction: number,
    series: any,
  ) {
    const modulationPoints: any[] = [];

    let dir = direction;
    if (this.copiedPoint && this.firstCopiedPoint)
      dir = this.copiedPoint.x < this.firstCopiedPoint.x ? -1 : 1;

    modulationLines.forEach((line: CursorPlotLine, index: number) => {
      if (this.dragModulationLine === line) {
        //если это dragLine
        //просто обновляем точку маркера
        const point = this.getModulationLinePoint(
          value,
          direction,
          index,
          bandWidth,
          extremes,
          series,
        );
        if (point) modulationPoints.push(point);
        return;
      }

      //иначе удаляем линию и перерисовываем
      if (dir === direction && index === 0) {
        return;
      }

      const shiftedValue = value + direction * (index + 1) * bandWidth;
      const isInExtreme = extremes.min <= shiftedValue && extremes.max >= shiftedValue;
      if (isInExtreme) {
        const intersectionPoint = this.getIntersectionPoint(shiftedValue, series);
        if (intersectionPoint) {
          const point = {x: shiftedValue, y: intersectionPoint.y};
          modulationPoints.push(point);
        }
      }
      if (this.isShowLine) line.redraw(shiftedValue);
    });
    return modulationPoints;
  }

  /*
    Получить точку пересечения модуляционной линии с графиком серии
  */
  getModulationLinePoint(
    value: number,
    direction: number,
    index: number,
    bandWidth: number,
    extremes: any,
    series: any,
  ): {x: number; y: number} | null {
    const shiftedValue = value + direction * (index + 1) * bandWidth;
    const isInExtreme = extremes.min <= shiftedValue && extremes.max >= shiftedValue;
    if (isInExtreme) {
      const intersectionPoint = this.getIntersectionPoint(shiftedValue, series);
      if (intersectionPoint) {
        return {
          x: shiftedValue,
          y: intersectionPoint.y,
        };
      }
    }
    return null;
  }

  /*
    Действия при очистке курсора
  */
  clear() {
    this.clearModulationTooltips();

    // удаляем с графика все модуляционные линии
    this.modulationCursorLeftLines
      .concat(this.modulationCursorRightLines)
      .forEach((line: CursorPlotLine) => line.remove());

    this.modulationCursorLeftLines = [];
    this.modulationCursorRightLines = [];

    // удаляем с графика серию с маркерами
    this.modulationMarkerSeries.forEach((series: any) => {
      this.chart.get(series.id)?.remove();
    });

    this.modulationMarkerSeries = [];
    super.clear();
  }

  /*
    Действия при перерисовке (любом изменении графика)
  */
  render() {
    super.render();

    // если из сравнения был удален график, а с ним и ось, то нужно убрать соответствующую серию маркеров
    this.modulationMarkerSeries = this.modulationMarkerSeries.filter((series: any) => {
      return this.chart.yAxis[series.yAxis] !== undefined;
    });

    this.redrawModulationTooltips();
  }

  /*
    Перерисовать отметки модуляционных маркеров
  */
  private redrawModulationTooltips() {
    this.clearModulationTooltips();

    if (!this.isShowTooltips) return;

    this.modulationMarkerSeries.forEach((series: any) => {
      series.points.forEach((p: any) => {
        const t = this.drawTooltip({x: p.x, y: p.y}, series.yAxis);
        this.modulationTooltips.push(t);
      });
    });
  }

  /*
    Очистить отметки модуляционных маркеров
  */
  private clearModulationTooltips() {
    this.modulationTooltips.forEach((tooltip: any) => {
      tooltip.destroy();
      tooltip = undefined;
    });
    this.modulationTooltips = [];
  }

  /*
    Переключение показа линии
  */
  showLine(show: boolean) {
    super.showLine(show);
    this.redrawModulation(false);
  }

  /*
    Переключение показа отметок
  */
  showTooltips(show: boolean) {
    super.showTooltips(show);
    this.redrawModulationTooltips();
  }

  /*
    Установка заморозки событий
  */
  freezeEvents(freeze: boolean) {
    super.freezeEvents(freeze);
    this.modulationCursorLeftLines
      .concat(this.modulationCursorRightLines)
      .forEach(line => line.freezeEvents(freeze));
  }
}
